// Utility functions for weather data processing

export const parseMetarWind = (metar) => {
  if (!metar) return null;
  
  // Find wind component in METAR using regex
  const windMatch = metar.match(/(\d{3})(\d{2,3})(?:G(\d{2,3}))?KT/);
  
  if (!windMatch) return null;
  
  const direction = parseInt(windMatch[1]);
  const speed = parseInt(windMatch[2]);
  const gust = windMatch[3] ? parseInt(windMatch[3]) : null;
  
  return {
    direction,
    speed,
    gust,
    maxWind: gust || speed
  };
};

export const getWindColor = (windData, cautionWind, maxWind) => {
  if (!windData) return '#000000'; // Default black
  
  const effectiveWind = windData.maxWind;
  
  if (effectiveWind > maxWind) {
    return '#dc3545'; // Red
  } else if (effectiveWind >= cautionWind) {
    return '#ff8c00'; // Orange
  } else {
    return '#28a745'; // Green
  }
};

export const getFlightCategoryColor = (category) => ({
  VFR: 'green',
  MVFR: 'blue',
  IFR: '#b30000',
  LIFR: '#e6007e'
}[String(category || '').toUpperCase()] || '#666');

const parseTafVisibility = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const text = String(value).trim().toUpperCase().replace(/SM$/, '').replace(/\s+/g, '');
  if (/^P\d+(?:\.\d+)?\+?$/.test(text) || /^\d+(?:\.\d+)?\+$/.test(text)) return Infinity;
  const lessThan = text.startsWith('M');
  const normalized = text.replace(/^[MP]/, '');
  const fraction = normalized.match(/^(\d+)\/(\d+)$/);
  const mixed = normalized.match(/^(\d+)(\d+)\/(\d+)$/);
  const numeric = fraction
    ? Number(fraction[1]) / Number(fraction[2])
    : mixed
      ? Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3])
      : Number(normalized);
  if (!Number.isFinite(numeric)) return null;
  return lessThan ? Math.max(0, numeric - 0.01) : numeric;
};

const getTafCeiling = (forecast) => {
  if (forecast?.ceiling != null && Number.isFinite(Number(forecast.ceiling))) return Number(forecast.ceiling);
  const ceilingLayers = (Array.isArray(forecast?.clouds) ? forecast.clouds : [])
    .filter(cloud => ['BKN', 'OVC', 'VV'].includes(String(cloud?.cover || '').toUpperCase()))
    .map(cloud => cloud?.base == null || cloud.base === '' ? null : Number(cloud.base))
    .filter(value => value != null && Number.isFinite(value));
  return ceilingLayers.length ? Math.min(...ceilingLayers) : null;
};

export const getTafFlightCategory = (forecast, settings = {}) => {
  const providerCategory = String(forecast?.fltCat || forecast?.flightCategory || '').toUpperCase();
  const ceiling = getTafCeiling(forecast);
  const visibility = parseTafVisibility(forecast?.visib);
  const categories = [];
  if (ceiling != null) categories.push(getTafSkyCategory({ cover: 'BKN', base: ceiling }));
  if (visibility != null) categories.push(getTafVisibilityCategory(visibility));
  const categoryRank = { VFR: 0, MVFR: 1, IFR: 2, LIFR: 3 };
  if (categories.length) return categories.sort((left, right) => categoryRank[right] - categoryRank[left])[0];
  return ['VFR', 'MVFR', 'IFR', 'LIFR'].includes(providerCategory) ? providerCategory : null;
};

export const getTafSkyCategory = (clouds) => {
  const layers = Array.isArray(clouds) ? clouds : [clouds];
  const ceilings = layers
    .filter(cloud => ['BKN', 'OVC', 'VV'].includes(String(cloud?.cover || '').toUpperCase()))
    .map(cloud => cloud?.base == null || cloud.base === '' ? null : Number(cloud.base))
    .filter(base => base != null && Number.isFinite(base));
  if (!ceilings.length) return null;
  const base = Math.min(...ceilings);
  if (base < 500) return 'LIFR';
  if (base < 1000) return 'IFR';
  if (base <= 3000) return 'MVFR';
  return 'VFR';
};

export const getTafVisibilityCategory = (visibility) => {
  const value = parseTafVisibility(visibility);
  if (value == null) return null;
  if (value < 1) return 'LIFR';
  if (value < 3) return 'IFR';
  if (value <= 5) return 'MVFR';
  return 'VFR';
};

export const formatWindDisplay = (windData) => {
  if (!windData) return 'N/A';
  
  const { direction, speed, gust } = windData;
  let display = `${direction.toString().padStart(3, '0')}°@${speed}kt`;
  
  if (gust && gust > speed) {
    display += ` G${gust}kt`;
  }
  
  return display;
};

const tafWeatherPhenomena = {
  DZ: 'drizzle',
  RA: 'rain',
  SN: 'snow',
  SG: 'snow grains',
  IC: 'ice crystals',
  PL: 'ice pellets',
  GR: 'hail',
  GS: 'small hail or snow pellets',
  UP: 'unknown precipitation',
  BR: 'mist',
  FG: 'fog',
  FU: 'smoke',
  VA: 'volcanic ash',
  DU: 'dust',
  SA: 'sand',
  HZ: 'haze',
  PY: 'spray',
  PO: 'dust or sand whirls',
  SQ: 'squalls',
  FC: 'funnel cloud or tornado',
  SS: 'sandstorm',
  DS: 'duststorm'
};

const tafWeatherDescriptors = {
  MI: 'shallow',
  PR: 'partial',
  BC: 'patches of',
  DR: 'low drifting',
  BL: 'blowing',
  SH: 'showers of',
  TS: 'thunderstorms with',
  FZ: 'freezing'
};

export const decodeTafWeather = (weatherString) => {
  if (!weatherString) return '';

  return String(weatherString).trim().toUpperCase().split(/\s+/).filter(Boolean).map((group) => {
    if (group === 'NSW') return 'No significant weather';

    let remaining = group;
    let intensity = '';
    let proximity = '';
    if (remaining.startsWith('+')) {
      intensity = 'Heavy';
      remaining = remaining.slice(1);
    } else if (remaining.startsWith('-')) {
      intensity = 'Light';
      remaining = remaining.slice(1);
    }
    if (remaining.startsWith('VC')) {
      proximity = 'in the vicinity';
      remaining = remaining.slice(2);
    }

    let descriptor = '';
    for (const code of Object.keys(tafWeatherDescriptors).sort((left, right) => right.length - left.length)) {
      if (remaining.startsWith(code)) {
        descriptor = tafWeatherDescriptors[code];
        remaining = remaining.slice(code.length);
        break;
      }
    }

    const phenomena = [];
    while (remaining.length >= 2) {
      const code = remaining.slice(0, 2);
      if (!tafWeatherPhenomena[code]) break;
      phenomena.push(tafWeatherPhenomena[code]);
      remaining = remaining.slice(2);
    }

    if (!phenomena.length) {
      if (proximity && descriptor === 'showers of') return 'Showers in the vicinity';
      if (proximity && descriptor === 'thunderstorms with') return 'Thunderstorms in the vicinity';
      return '';
    }
    const isPrecipitation = phenomena.some(phenomenon => ['drizzle', 'rain', 'snow', 'snow grains', 'ice pellets', 'hail', 'small hail or snow pellets', 'unknown precipitation'].includes(phenomenon));
    const parts = [];
    if (intensity) parts.push(intensity);
    else if (isPrecipitation) parts.push('Moderate');
    if (descriptor) parts.push(descriptor);
    parts.push(phenomena.join(' and '));
    let decoded = parts.join(' ');
    if (proximity) decoded += ` ${proximity}`;
    return decoded.charAt(0).toUpperCase() + decoded.slice(1);
  }).filter(Boolean).join(', ');
};